// This file is what is used to populate the main.js file. It references all browser.js files (which in turn includes any index.js and styles.css file they reference) and anything else directly referenced here.
// TODO: how to only include the specific lines of a style.css file that are referenced, not every class defined in the file
import Campaigner from "../components/campaigner/browser";

// Set campaign cookies
new Campaigner({
  id: "cid",
  cookieName: ["cid", "CID"],
  cookieDays: 365
});

new Campaigner({
  id: "mdcid",
  cookieName: ["mdcid", "MDCID"],
  cookieDays: null
});

window.cuc = window.cuc || {}; // setup a place we can store some "global" (in a cuc scope) stuff
const mediaPlayerOptions = {
  youTubeOptions: {
    playsInline: 0
  }
};

const criticalComponentTypes = new Set(["icon", "svg", "unveil"]);
const selectorBasedComponents = [
  {
    type: "remote-trigger",
    selector: "[data-remote-trigger-id], [data-secondary-remote-trigger-id]"
  }
];

// Require all the component "browser.js" files lazily so only the ones used on the
// current page are parsed and executed.
const loadedComponents = {};
const componentPromises = {};
const componentLoaders = {};
const requireJS = require.context("../components", true, /browser\.js?$/);
requireJS.keys().forEach((filename) => {
  const type = filename.match(/^\.\/(?:.*\/)?([-a-z0-9]+)\/browser\.js?$/)?.[1];

  if (type) {
    componentLoaders[type] = () => Promise.resolve(requireJS(filename));
  }
});

// save off a copy of the components into a global so they can be re-run as needed
window.cuc.components = loadedComponents;

const getInitializedAttribute = (type) =>
  type.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase()) +
  "Initialized";

const rootMatchesSelector = (root, selector) => {
  if (!root || typeof root.matches !== "function") {
    return false;
  }

  return root.matches(selector);
};

const rootContainsSelector = (root, selector) => {
  if (root === document || root === document?.documentElement) {
    return !!document.querySelector(selector);
  }

  return (
    rootMatchesSelector(root, selector) || !!root.querySelector?.(selector)
  );
};

const getElementsForType = (root, type) => {
  const selector = `[data-type="${type}"]`;
  const elements = [];

  if (rootMatchesSelector(root, selector)) {
    elements.push(root);
  }

  if (typeof root?.querySelectorAll === "function") {
    elements.push(...root.querySelectorAll(selector));
  }

  return elements;
};

const collectComponentTypes = (root = document) => {
  const types = new Set();

  if (root === document?.documentElement) {
    types.add("page");
  }

  if (rootMatchesSelector(root, "[data-type]")) {
    types.add(root.getAttribute("data-type"));
  }

  if (typeof root?.querySelectorAll === "function") {
    root.querySelectorAll("[data-type]").forEach((element) => {
      types.add(element.getAttribute("data-type"));
    });
  }

  selectorBasedComponents.forEach(({ type, selector }) => {
    if (rootContainsSelector(root, selector)) {
      types.add(type);
    }
  });

  return [...types].filter(
    (type) =>
      type && !criticalComponentTypes.has(type) && componentLoaders[type]
  );
};

const loadComponent = (type, { reinit = false } = {}) => {
  if (!componentLoaders[type]) {
    return Promise.resolve(null);
  }

  if (!componentPromises[type]) {
    componentPromises[type] = componentLoaders[type]().then((module) => {
      if (type === "media-player" && typeof module.default === "function") {
        module.default(mediaPlayerOptions);
      }

      loadedComponents[type] = module;
      return module;
    });
  }

  return componentPromises[type].then((module) => {
    if (reinit && typeof module?.init === "function") {
      module.init();
    }

    return module;
  });
};

const setupLoadedElements = (root, type, module) => {
  if (typeof module?.default !== "function") {
    return;
  }

  const initializedAttribute = getInitializedAttribute(type);

  getElementsForType(root, type).forEach((element) => {
    if (!element.hasAttribute(initializedAttribute)) {
      new module.default(element);
      element.setAttribute(initializedAttribute, "");
    }
  });
};

const initComponents = async (
  root = document,
  { reinitLoaded = false } = {}
) => {
  const types = collectComponentTypes(root);

  await Promise.all(
    types.map(async (type) => {
      const wasLoaded = !!loadedComponents[type];
      const module = await loadComponent(type, {
        reinit: reinitLoaded && wasLoaded
      });

      if (reinitLoaded && wasLoaded && typeof module?.init !== "function") {
        setupLoadedElements(root, type, module);
      }
    })
  );
};

// For a given DOM element, finds the corresponding browser constructor and calls it.
export function setup(element) {
  return initComponents(element, { reinitLoaded: true });
}

// Handles rendering the given `data`, adding it to the innerHTML of `container`, and then calling the component's constructor so it's browser JS is setup.
export async function render(container, data) {
  if (typeof data === "string") {
    container.innerHTML = data;
    await initComponents(container, { reinitLoaded: true });
  } else {
    const { type } = data;

    if (!type) {
      return;
    }

    const module = await loadComponent(type);
    const { render } = module || {};

    if (render) {
      container.innerHTML = render(data);
      await initComponents(container, { reinitLoaded: true });
    }
  }
}

// setup a way to re-run all inits. Not wise at this point as many need logic added to not run on the same component multiple times, but is here for if someone has a specific reason to do it
window.cuc.loadComponent = loadComponent;
window.cuc.reInit = () => initComponents(document, { reinitLoaded: true });

// setup one for the fab for some AB testing. Could likely remove after 2020 LTW is done
window.cuc.setupFab = () =>
  Promise.all(
    [
      "cookie-based-display",
      "floating-action-button",
      "popup",
      "remote-trigger",
      "speed-dial",
      "icon"
    ].map((type) => loadComponent(type, { reinit: true }))
  );

const start = () => {
  initComponents(document).catch((error) => {
    console.error("Failed to initialize browser components", error);
  });
};

document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", start, { once: true })
  : start();
